import React from 'react';
import {FormattedHTMLMessage} from 'react-intl';
import {BUDGET_MONEY_VIEWS, BUDGET_VIEWS} from '../../../../../constants';

const baselineDescription = formatMessage => {
	return {
		title: formatMessage({id: 'project_budget.baseline_definition_title'}),
		description: formatMessage({id: 'project_budget.baseline_definition_text'}),
	};
};
const fixedPriceDescription = formatMessage => {
	return {
		title: formatMessage({id: 'project_budget.fixed_price_definition'}),
		description: <FormattedHTMLMessage id={'project_budget.fixed_price_definition_description'} />,
	};
};
const varianceDescription = formatMessage => {
	return {
		title: formatMessage({id: 'project_budget.variance_definition'}),
		description: formatMessage({id: 'project_budget.variance_definition_description'}),
	};
};
export const offsetTooltip = formatMessage => {
	return [
		{
			title: formatMessage({id: 'project_budget.offset_definition'}),
			description: <FormattedHTMLMessage id={'project_budget.offset_definition_description'} />,
		},
	];
};

const ActualTooltips = (formatMessage, budgetView, budgetMoneyView, showFixedPriceTooltip) => {
	if (budgetView === BUDGET_VIEWS.MONEY) {
		switch (budgetMoneyView) {
			case BUDGET_MONEY_VIEWS.EXPENSES:
				return {
					actualRevenue: [
						{
							title: formatMessage({id: 'project_budget.revenue'}),
							details: [formatMessage({id: 'project_budget.price_past_planned_expenses'})],
						},
					],
					actualCost: [
						{
							title: formatMessage({id: 'common.cost'}),
							details: [formatMessage({id: 'project_budget.cost_past_expenses'})],
						},
					],
					actualProfit: [
						{
							title: formatMessage({id: 'common.profit'}),
							details: [
								formatMessage({id: 'project_budget.actual_revenue'}),
								'-',
								formatMessage({id: 'insights.component.list.column.actualCost'}),
							],
						},
					],
				};
			case BUDGET_MONEY_VIEWS.WORK_AND_EXPENSES:
				return {
					actualRevenue: [
						showFixedPriceTooltip ? fixedPriceDescription(formatMessage) : {hide: true},
						{
							title: formatMessage({id: 'project_budget.revenue'}),
							details: showFixedPriceTooltip
								? [
										formatMessage({id: 'project_budget.done_task_value'}),
										'/',
										formatMessage({id: 'project_budget.total_task_value'}),
										'x',
										formatMessage({id: 'common.fixed_price'}),
										'+',
										formatMessage({id: 'project_budget.price_past_planned_expenses'}),
								  ]
								: [
										formatMessage({id: 'project_budget.all_billable_time_entries'}),
										'x',
										formatMessage({id: 'project_budget.role_rate'}),
										'+',
										formatMessage({id: 'project_budget.price_past_planned_expenses'}),
								  ],
						},
					],
					actualCost: [
						{
							title: formatMessage({id: 'common.cost'}),
							details: [
								formatMessage({id: 'project_budget.all_time_registrations'}),
								'x',
								formatMessage({id: 'project_budget.internal_cost'}),
								'+',
								formatMessage({id: 'project_budget.cost_past_expenses'}),
							],
						},
					],
					actualProfit: [
						{
							title: formatMessage({id: 'common.profit'}),
							details: [
								formatMessage({id: 'project_budget.actual_revenue'}),
								'-',
								formatMessage({id: 'insights.component.list.column.actualCost'}),
							],
						},
					],
					actualMargin: [
						{
							title: formatMessage({id: 'common.margin'}),
							details: [
								formatMessage({id: 'project_budget.actual_profit'}),
								'/',
								formatMessage({id: 'project_budget.actual_revenue'}),
								'x',
								'100',
							],
						},
					],
				};
		}
	} else {
		return {
			registeredMinutes: [{details: [formatMessage({id: 'project_budget.all_time_entries'})]}],
		};
	}
};

const PlanTooltips = (formatMessage, budgetView, budgetMoneyView, showFixedPriceTooltip) => {
	if (budgetView === BUDGET_VIEWS.MONEY) {
		switch (budgetMoneyView) {
			case BUDGET_MONEY_VIEWS.EXPENSES:
				return {
					plannedRevenue: [
						{
							title: formatMessage({id: 'project_budget.revenue'}),
							details: [formatMessage({id: 'project_budget.price_all_planned_expenses'})],
						},
					],
					plannedCost: [
						{
							title: formatMessage({id: 'common.cost'}),
							details: [formatMessage({id: 'project_budget.cost_all_planned_expenses'})],
						},
					],
					plannedProfit: [
						{
							title: formatMessage({id: 'common.profit'}),
							details: [
								formatMessage({id: 'project_budget.plan_revenue'}),
								'-',
								formatMessage({id: 'project_budget.plan_cost'}),
							],
						},
					],
				};
			case BUDGET_MONEY_VIEWS.WORK_AND_EXPENSES:
				return {
					plannedRevenue: [
						showFixedPriceTooltip ? fixedPriceDescription(formatMessage) : {hide: true},
						{
							title: formatMessage({id: 'project_budget.revenue'}),
							details: showFixedPriceTooltip
								? [
										formatMessage({id: 'common.fixed_price'}),
										'+',
										formatMessage({id: 'project_budget.price_all_planned_expenses'}),
								  ]
								: [
										formatMessage({id: 'project_budget.all_billable_forecast_hours'}),
										'x',
										formatMessage({id: 'project_budget.role_rate'}),
										'+',
										formatMessage({id: 'project_budget.price_all_planned_expenses'}),
								  ],
						},
					],
					plannedCost: [
						{
							title: formatMessage({id: 'common.cost'}),
							details: showFixedPriceTooltip
								? [
										formatMessage({id: 'project_budget.task_estimate'}),
										'x',
										formatMessage({id: 'project_budget.internal_cost'}),
										'+',
										formatMessage({id: 'project_budget.cost_all_planned_expenses'}),
								  ]
								: [
										formatMessage({id: 'project_budget.task_estimate'}),
										'x',
										formatMessage({id: 'project_budget.internal_cost'}),
										'+',
										formatMessage({id: 'project_budget.cost_all_planned_expenses'}),
								  ],
						},
					],
					plannedProfit: [
						{
							title: formatMessage({id: 'common.profit'}),
							details: [
								formatMessage({id: 'project_budget.plan_revenue'}),
								'-',
								formatMessage({id: 'project_budget.plan_cost'}),
							],
						},
					],
					plannedMargin: [
						{
							title: formatMessage({id: 'common.margin'}),
							details: [
								formatMessage({id: 'project_budget.planned_profit'}),
								'/',
								formatMessage({id: 'project_budget.planned_revenue'}),
								'x',
								'100',
							],
						},
					],
				};
		}
	} else {
		return {
			scopeApprovedMinutes: [{details: [formatMessage({id: 'project_budget.all_forecast_hours'})]}],
		};
	}
};

const RemainingTooltips = (formatMessage, budgetView, budgetMoneyView, showFixedPriceTooltip) => {
	if (budgetView === BUDGET_VIEWS.MONEY) {
		switch (budgetMoneyView) {
			case BUDGET_MONEY_VIEWS.EXPENSES:
				return {
					remainingRevenue: [
						{
							title: formatMessage({id: 'project_budget.revenue'}),
							details: [formatMessage({id: 'project_budget.price_future_planned_expenses'})],
						},
					],
					remainingCost: [
						{
							title: formatMessage({id: 'common.cost'}),
							details: [formatMessage({id: 'project_budget.cost_future_planned_expenses'})],
						},
					],
					remainingProfit: [
						{
							title: formatMessage({id: 'common.profit'}),
							details: [
								formatMessage({id: 'project_budget.remaining_revenue'}),
								'-',
								formatMessage({id: 'project_budget.remaining_cost'}),
							],
						},
					],
				};
			case BUDGET_MONEY_VIEWS.WORK_AND_EXPENSES:
				return {
					remainingRevenue: [
						showFixedPriceTooltip ? fixedPriceDescription(formatMessage) : {hide: true},
						{
							title: formatMessage({id: 'project_budget.revenue'}),
							details: showFixedPriceTooltip
								? [
										formatMessage({id: 'project_budget.not_done_task_value'}),
										'/',
										formatMessage({id: 'project_budget.total_task_value'}),
										'x',
										formatMessage({id: 'common.fixed_price'}),
										'+',
										formatMessage({id: 'project_budget.price_future_planned_expenses'}),
								  ]
								: [
										formatMessage({id: 'project_budget.all_remaining_billable_hours'}),
										'x',
										formatMessage({id: 'project_budget.role_rate'}),
										'+',
										formatMessage({id: 'project_budget.price_future_expenses'}),
								  ],
						},
					],
					remainingCost: [
						{
							title: formatMessage({id: 'common.cost'}),
							details: [
								formatMessage({id: 'project_budget.remaining_task_estimate'}),
								'x',
								formatMessage({id: 'project_budget.internal_cost'}),
								'+',
								formatMessage({id: 'project_budget.cost_future_planned_expenses'}),
							],
						},
					],
					remainingProfit: [
						{
							title: formatMessage({id: 'common.profit'}),
							details: [
								formatMessage({id: 'project_budget.remaining_revenue'}),
								'-',
								formatMessage({id: 'project_budget.remaining_cost'}),
							],
						},
					],
					remainingMargin: [
						{
							title: formatMessage({id: 'common.margin'}),
							details: [
								formatMessage({id: 'project_budget.remaining_profit'}),
								'/',
								formatMessage({id: 'project_budget.remaining_revenue'}),
								'x',
								'100',
							],
						},
					],
				};
		}
	} else {
		return {
			remainingMinutes: [{details: [formatMessage({id: 'project_budget.all_remaining_hours'})]}],
		};
	}
};

const ForecastTooltips = (formatMessage, budgetView) => {
	if (budgetView === BUDGET_VIEWS.MONEY) {
		return {
			forecastRevenue: [
				{
					title: formatMessage({id: 'project_budget.revenue'}),
					details: [
						formatMessage({id: 'project_budget.actual_revenue'}),
						'+',
						formatMessage({id: 'project_budget.remaining_revenue'}),
					],
				},
			],
			forecastCost: [
				{
					title: formatMessage({id: 'common.cost'}),
					details: [
						formatMessage({id: 'insights.component.list.column.actualCost'}),
						'+',
						formatMessage({id: 'project_budget.remaining_cost'}),
					],
				},
			],
			forecastProfit: [
				{
					title: formatMessage({id: 'common.profit'}),
					details: [
						formatMessage({id: 'project_budget.forecast_revenue'}),
						'-',
						formatMessage({id: 'project_budget.forecast_cost'}),
					],
				},
			],
			forecastMargin: [
				{
					title: formatMessage({id: 'common.margin'}),
					details: [
						formatMessage({id: 'project_budget.forecast_profit'}),
						'/',
						formatMessage({id: 'project_budget.forecast_revenue'}),
						'x',
						'100',
					],
				},
			],
		};
	} else {
		return {
			forecastMinutes: [
				{
					details: [formatMessage({id: 'project_budget.actuals'}), '+', formatMessage({id: 'common.remaining'})],
				},
			],
		};
	}
};

const ActualVsPlanTooltips = (formatMessage, budgetView) => {
	if (budgetView === BUDGET_VIEWS.MONEY) {
		return {
			actualVsPlanRevenue: [
				varianceDescription(formatMessage),
				{
					title: formatMessage({id: 'project_budget.revenue'}),
					details: [
						formatMessage({id: 'project_budget.actual_revenue'}),
						'-',
						formatMessage({id: 'project_budget.planned_revenue'}),
					],
				},
			],
			actualVsPlanCost: [
				varianceDescription(formatMessage),
				{
					title: formatMessage({id: 'common.cost'}),
					details: [
						formatMessage({id: 'insights.component.list.column.actualCost'}),
						'-',
						formatMessage({id: 'project_budget.planned_cost'}),
					],
				},
			],
			actualVsPlanProfit: [
				varianceDescription(formatMessage),
				{
					title: formatMessage({id: 'common.profit'}),
					details: [
						formatMessage({id: 'project_budget.variance_revenue'}),
						'-',
						formatMessage({id: 'project_budget.variance_cost'}),
					],
				},
			],
		};
	} else {
		return {
			registeredVsScopeApprovedMinutes: [
				varianceDescription(formatMessage),
				{details: [formatMessage({id: 'project_budget.actuals'}), '-', formatMessage({id: 'common.planned'})]},
			],
		};
	}
};

const ForecastVsPlanTooltips = (formatMessage, budgetView) => {
	if (budgetView === BUDGET_VIEWS.MONEY) {
		return {
			forecastVsPlanRevenue: [
				varianceDescription(formatMessage),
				{
					title: formatMessage({id: 'project_budget.revenue'}),
					details: [
						formatMessage({id: 'project_budget.forecast_revenue'}),
						'-',
						formatMessage({id: 'project_budget.planned_revenue'}),
					],
				},
			],
			forecastVsPlanCost: [
				varianceDescription(formatMessage),
				{
					title: formatMessage({id: 'common.cost'}),
					details: [
						formatMessage({id: 'project_budget.forecast_cost'}),
						'-',
						formatMessage({id: 'project_budget.planned_cost'}),
					],
				},
			],
			forecastVsPlanProfit: [
				varianceDescription(formatMessage),
				{
					title: formatMessage({id: 'common.profit'}),
					details: [
						formatMessage({id: 'project_budget.variance_revenue'}),
						'-',
						formatMessage({id: 'project_budget.variance_cost'}),
					],
				},
			],
		};
	} else {
		return {
			forecastVsScopeApprovedMinutes: [
				varianceDescription(formatMessage),
				{
					details: [
						formatMessage({id: 'project_budget.actuals'}),
						'+',
						formatMessage({id: 'common.remaining'}),
						'-',
						formatMessage({id: 'common.planned'}),
					],
				},
			],
		};
	}
};

const BaselineTooltips = (formatMessage, budgetView, budgetMoneyView) => {
	if (budgetView === BUDGET_VIEWS.MONEY) {
		switch (budgetMoneyView) {
			case BUDGET_MONEY_VIEWS.EXPENSES:
				return {
					baselineRevenue: [
						baselineDescription(formatMessage),
						{
							title: formatMessage({id: 'project_budget.revenue'}),
							details: [formatMessage({id: 'project_budget.expense_revenue'})],
						},
					],
					baselineCost: [
						baselineDescription(formatMessage),
						{
							title: formatMessage({id: 'common.cost'}),
							details: [formatMessage({id: 'project_budget.expense_cost'})],
						},
					],
					baselineProfit: [
						baselineDescription(formatMessage),
						{
							title: formatMessage({id: 'common.profit'}),
							details: [
								formatMessage({id: 'project_budget.baseline_revenue'}),
								'-',
								formatMessage({id: 'project_budget.baseline_cost'}),
							],
						},
					],
				};
			case BUDGET_MONEY_VIEWS.WORK_AND_EXPENSES:
				return {
					baselineRevenue: [
						baselineDescription(formatMessage),
						{
							title: formatMessage({id: 'project_budget.revenue'}),
							details: [
								formatMessage({id: 'common.total_estimate'}),
								'x',
								formatMessage({id: 'project_budget.role_rate'}),
								'+',
								formatMessage({id: 'project_budget.expense_revenue'}),
							],
						},
					],
					baselineCost: [
						baselineDescription(formatMessage),
						{
							title: formatMessage({id: 'common.cost'}),
							details: [
								formatMessage({id: 'common.total_estimate'}),
								'x',
								formatMessage({id: 'project_budget.adjusted_cost_rate'}),
								'+',
								formatMessage({id: 'project_budget.expense_cost'}),
							],
						},
					],
					baselineProfit: [
						baselineDescription(formatMessage),
						{
							title: formatMessage({id: 'common.profit'}),
							details: [
								formatMessage({id: 'project_budget.baseline_revenue'}),
								'-',
								formatMessage({id: 'project_budget.baseline_cost'}),
							],
						},
					],
					baselineMargin: [
						{
							title: formatMessage({id: 'common.margin'}),
							details: [
								formatMessage({id: 'project_budget.baseline_profit'}),
								'/',
								formatMessage({id: 'project_budget.baseline_revenue'}),
								'x',
								'100',
							],
						},
					],
				};
		}
	} else {
		return {
			baselineMinutes: [
				baselineDescription(formatMessage),
				{
					details: [formatMessage({id: 'common.total_estimate'})],
				},
			],
		};
	}
};

const BaselineVsForecastTooltips = (formatMessage, budgetView) => {
	if (budgetView === BUDGET_VIEWS.MONEY) {
		return {
			baselineVsForecastRevenue: [
				varianceDescription(formatMessage),
				{
					title: formatMessage({id: 'project_budget.revenue'}),
					details: [
						formatMessage({id: 'project_budget.baseline_revenue'}),
						'-',
						formatMessage({id: 'project_budget.forecast_revenue'}),
					],
				},
			],
			baselineVsForecastCost: [
				varianceDescription(formatMessage),
				{
					title: formatMessage({id: 'common.cost'}),
					details: [
						formatMessage({id: 'project_budget.baseline_cost'}),
						'-',
						formatMessage({id: 'project_budget.forecast_cost'}),
					],
				},
			],
			baselineVsForecastProfit: [
				varianceDescription(formatMessage),
				{
					title: formatMessage({id: 'common.profit'}),
					details: [
						formatMessage({id: 'project_budget.variance_revenue'}),
						'-',
						formatMessage({id: 'project_budget.variance_cost'}),
					],
				},
			],
		};
	} else {
		return {
			baselineVsForecastMinutes: [
				varianceDescription(formatMessage),
				{
					details: [
						formatMessage({id: 'common.total_estimate'}),
						'-',
						formatMessage({id: 'project_budget.actuals'}),
						'+',
						formatMessage({id: 'common.remaining'}),
					],
				},
			],
		};
	}
};

const BaselineVsActualTooltips = (formatMessage, budgetView) => {
	if (budgetView === BUDGET_VIEWS.MONEY) {
		return {
			baselineVsActualRevenue: [
				varianceDescription(formatMessage),
				{
					title: formatMessage({id: 'project_budget.revenue'}),
					details: [
						formatMessage({id: 'project_budget.baseline_revenue'}),
						'-',
						formatMessage({id: 'project_budget.actual_revenue'}),
					],
				},
			],
			baselineVsActualCost: [
				varianceDescription(formatMessage),
				{
					title: formatMessage({id: 'common.cost'}),
					details: [
						formatMessage({id: 'project_budget.baseline_cost'}),
						'-',
						formatMessage({id: 'insights.component.list.column.actualCost'}),
					],
				},
			],
			baselineVsActualProfit: [
				varianceDescription(formatMessage),
				{
					title: formatMessage({id: 'common.profit'}),
					details: [
						formatMessage({id: 'project_budget.variance_revenue'}),
						'-',
						formatMessage({id: 'project_budget.variance_cost'}),
					],
				},
			],
		};
	} else {
		return {
			baselineVsRegisteredMinutes: [
				varianceDescription(formatMessage),
				{
					details: [formatMessage({id: 'common.total_estimate'}), '-', formatMessage({id: 'project_budget.actuals'})],
				},
			],
		};
	}
};

const InvoicedVsActualTooltips = formatMessage => {
	return {
		invoicedVsActualRevenue: [
			varianceDescription(formatMessage),
			{
				title: formatMessage({id: 'common.variance'}),
				details: [
					formatMessage({id: 'project_section.invoice'}),
					'-',
					formatMessage({id: 'project_budget.actual_revenue'}),
				],
			},
		],
	};
};

const TotalsTooltips = (formatMessage, budgetView, budgetMoneyView, showFixedPriceTooltip) => {
	return {
		...BaselineTooltips(formatMessage, budgetView, budgetMoneyView),
		...BaselineVsActualTooltips(formatMessage, budgetView),
		...BaselineVsForecastTooltips(formatMessage, budgetView),
		...ActualTooltips(formatMessage, budgetView, budgetMoneyView, showFixedPriceTooltip),
		...PlanTooltips(formatMessage, budgetView, budgetMoneyView, showFixedPriceTooltip),
		...ActualVsPlanTooltips(formatMessage, budgetView),
		...ForecastVsPlanTooltips(formatMessage, budgetView),
		...RemainingTooltips(formatMessage, budgetView, budgetMoneyView, showFixedPriceTooltip),
		...ForecastTooltips(formatMessage, budgetView),
	};
};

export const BudgetTooltips = {
	BaselineTooltips,
	ActualTooltips,
	PlanTooltips,
	ActualVsPlanTooltips,
	RemainingTooltips,
	ForecastTooltips,
	ForecastVsPlanTooltips,
	BaselineVsActualTooltips,
	BaselineVsForecastTooltips,
	InvoicedVsActualTooltips,
	TotalsTooltips,
};
